import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { useCallback, useEffect, useState } from 'react'

import { API_URL } from '@goodfellas/environment'
import * as Belt from '@goodfellas/graduation'
import { digitsOnly } from '@goodfellas/phone-identity/normalize'

import { goodfellasApi } from '../../apis'
import { useAuth } from '../../contexts/AuthContext'
import { fetchGroups, fetchTableData } from '../../functions/dataHelpers'
import { normalizeBeltColor, normalizeDegree } from '../../functions/graduation'
import {
  sendRecoverPasswordCode,
  sendSsoCode,
  verifyPhoneLogin,
  verifyRecoverPassword,
  verifySsoCode,
} from '../../functions/ssoCode'
import {
  FighterCard,
  User,
  useCardsStore,
  useFeedbackStore,
  useStore,
  useTableGroupsStore,
  useTableStore,
} from '../../functions/store'
import { isEqualWithNinthDigit } from '../../functions/utils'
import { FighterType } from '../../types/fighter'
import BeltInput from './beltInput'
import CountryPhoneInput from './countryPhoneInput'
import SsoCodeInput from './ssoCodeInput'

export default function IdentificatorBox() {
  const router = useRouter()
  const { login, token, phone } = useAuth()
  const [userPhone, setUserPhone] = useState<string>('')
  const [userBelt, setUserBelt] = useState<string>('')
  const [formError, setFormError] = useState<string | null>(null)
  const user = useStore((state) => state)
  const tableGroups = useTableGroupsStore((state) => state)
  const tableStore = useTableStore((state) => state)
  const feedbackStore = useFeedbackStore((state) => state)
  const cardsStore = useCardsStore((state) => state)
  const { t } = useTranslation('avaliacao360')
  const [ssoCodeConfig, setSsoCodeConfig] = useState<{
    show: boolean
    code: string
    screen: 'sendCode' | 'login'
  }>({
    show: false,
    code: '',
    screen: 'sendCode',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isRecoverPassword, setIsRecoverPassword] = useState(false)

  const getTheStep = () => {
    // Always start with home page after login
    return 'home'
  }

  const getUserInfo = useCallback(async () => {
    setFormError(null)
    setIsLoading(true)
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let groups: any[] = []
      try {
        groups = await fetchGroups()
        tableGroups.setGroups(groups)
      } catch (_err) {
        // non-fatal; we can still try to fetch the user's table without groupId
      }

      // When link has ?avaliar=PHONE, resolve group that contains both evaluator and target so we load the right list and card
      const rawAvaliar = router.query?.avaliar
      const targetPhoneFromQuery =
        typeof rawAvaliar === 'string'
          ? digitsOnly(rawAvaliar)
          : Array.isArray(rawAvaliar)
          ? digitsOnly(rawAvaliar[0] ?? '')
          : ''
      let suggestedGroupId: string | undefined
      if (targetPhoneFromQuery.length >= 10) {
        try {
          const resolveRes = await goodfellasApi.post(
            '/evaluation/resolve/phone',
            {
              phoneDigits: targetPhoneFromQuery,
            },
          )
          if (resolveRes?.data?.suggestedGroupId) {
            suggestedGroupId = resolveRes.data.suggestedGroupId
          }
        } catch (_err) {
          // non-fatal; fall back to default table load
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let fetchedData: any = null
      if (suggestedGroupId) {
        try {
          fetchedData = await fetchTableData(
            phone ?? userPhone,
            suggestedGroupId,
          )
        } catch (_err) {
          fetchedData = null
        }
      }
      if (!fetchedData) {
        try {
          // Prefer backend auto-resolution (no groupId) to avoid assuming groups[0]
          fetchedData = await fetchTableData(phone ?? userPhone)
        } catch (_err) {
          fetchedData = null
        }
      }

      // Canonical graduation from the platform profile (if available).
      // This prevents first-render mismatches (header vs panels) when groupTables has stale/empty belt.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let profileMe: any = null
      try {
        const response = await goodfellasApi.get('/evaluation/resolve/me', {
          validateStatus: () => true,
        })
        if (response.status >= 200 && response.status < 300) {
          profileMe = response.data
        }
      } catch (_err) {
        profileMe = null
      }

      // Fallback: if we managed to fetch groups, try with the first group id
      if (!fetchedData && groups?.[0]?._id) {
        try {
          fetchedData = await fetchTableData(
            phone ?? userPhone,
            groups?.[0]?._id,
          )
        } catch (_err) {
          fetchedData = null
        }
      }

      if (!fetchedData) {
        setFormError(
          'Não conseguimos carregar seus dados agora. Se você já está em um grupo com a Jiulia, tente novamente em alguns segundos. Se persistir, a base do ambiente local pode não estar atualizada.',
        )
        return
      }

      const currentStep = getTheStep()

      const getUserInfoFromData = () => {
        // For home/table steps, get user from groupTable data
        if (currentStep === 'home' || currentStep === 'table') {
          const foundUser = fetchedData?.users?.find((u) =>
            isEqualWithNinthDigit(u.phoneNumber, phone ?? userPhone),
          )
          return foundUser
        } else if (currentStep === 'feedback') {
          const user = {
            name: fetchedData.name,
            belt: fetchedData.belt,
            degree: fetchedData.degree,
            type: fetchedData.type,
          }
          return user
        }
        return null
      }

      const selectedGroup =
        (fetchedData?._id &&
          groups?.find(
            (g) => g?._id?.toString?.() === fetchedData?._id?.toString?.(),
          )) ||
        groups?.[0] ||
        (fetchedData?._id && fetchedData?.group
          ? {
              _id: fetchedData?._id?.toString?.() ?? fetchedData?._id,
              group: fetchedData?.group,
            }
          : undefined)

      // Get user info from fetched data
      const userInfo = getUserInfoFromData()

      const profileBelt = normalizeBeltColor(profileMe?.beltColor)
      const profileDegree = normalizeDegree(profileBelt, profileMe?.degree)

      const tableBelt = normalizeBeltColor(userInfo?.belt)
      const tableDegree = normalizeDegree(tableBelt, userInfo?.degree)

      const inputBelt = normalizeBeltColor(userBelt)

      // Canonical belt: profile_user (resolve/me) first, then group table, then store.
      // On login the user can override with inputBelt; then degrees reset by source.
      const resolvedBelt =
        inputBelt ||
        profileBelt ||
        tableBelt ||
        normalizeBeltColor(user.belt) ||
        ''
      const resolvedDegree = (() => {
        if (inputBelt) {
          if (inputBelt && inputBelt === profileBelt) return profileDegree
          if (inputBelt && inputBelt === tableBelt) return tableDegree
          return 0
        }
        if (profileBelt) return profileDegree
        if (tableBelt) return tableDegree
        return normalizeDegree(normalizeBeltColor(user.belt), user.degree)
      })()

      const newUser: User = {
        ...user,
        phoneNumber: phone ?? userPhone,
        belt: resolvedBelt,
        name: userInfo?.name || user.name,
        degree: resolvedDegree,
        type: userInfo?.type || user.type,
        step: currentStep,
        group: selectedGroup,
      }
      user.setUser(newUser)

      // If user's belt/degree are missing in groupTables, persist it so the team table can show it.
      // This makes each athlete's belt available once they log in and pick their belt.
      try {
        const groupId = fetchedData?._id?.toString?.() ?? fetchedData?._id
        const tableUserRecord =
          fetchedData?.users?.find((u) =>
            isEqualWithNinthDigit(u.phoneNumber, phone ?? userPhone),
          ) ?? null

        const tableRecordBelt = normalizeBeltColor(tableUserRecord?.belt)
        const tableRecordDegree = Number(tableUserRecord?.degree ?? 0)
        const needsPersist =
          Boolean(groupId) &&
          Boolean(resolvedBelt) &&
          (tableRecordBelt !== resolvedBelt ||
            normalizeDegree(resolvedBelt, tableRecordDegree) !== resolvedDegree)

        if (needsPersist) {
          await goodfellasApi.post(`${API_URL}evaluation/tables`, {
            phoneNumber: phone ?? userPhone,
            name: newUser.name,
            belt: resolvedBelt,
            degree: resolvedDegree,
            type: newUser.type || '',
            groupId,
          })

          // Best-effort: update local table store if present so UI reflects without refresh
          if (fetchedData?.users && fetchedData?._id && fetchedData?.group) {
            const patchedUsers = fetchedData.users.map((u) => {
              if (!isEqualWithNinthDigit(u.phoneNumber, phone ?? userPhone))
                return u
              return {
                ...u,
                belt: resolvedBelt,
                degree: resolvedDegree,
                type: newUser.type || u.type,
              }
            })

            tableStore.setGroup({
              _id: fetchedData._id,
              groupName: fetchedData.group,
              fighters: patchedUsers.map((u) => ({
                phoneNumber: u.phoneNumber,
                name: u.name,
                belt: u.belt,
                degree: u.degree,
                type: u.type,
              })),
            })
          }
        }
      } catch (err) {
        // Non-fatal: table still works; belt can be filled manually later
        console.debug('Could not persist user belt to group table', err)
      }

      if (currentStep === 'table') {
        const newTable = {
          group: {
            _id: fetchedData._id,
            groupName: fetchedData.group,
            fighters: fetchedData.users.map((user) => ({
              phoneNumber: user.phoneNumber,
              name: user.name,
              belt: user.belt,
              degree: user.degree,
              type: user.type,
            })),
          },
          user: newUser,
          setGroup: tableStore.setGroup,
          updateFighterData: tableStore.updateFighterData,
        }
        tableStore.setGroup(newTable.group)
      } else if (currentStep === 'feedback') {
        const newFeedback = {
          date: fetchedData.date,
          phoneNumber: phone ?? userPhone,
          name: fetchedData.name,
          belt: userBelt,
          degree: fetchedData.degree,
          type: fetchedData.type,
          ratings: fetchedData.ratings.map((fighter) => ({
            fighterPhone: fighter.fighterPhone,
            fighterName: fighter.fighterName,
            fighterBelt: fighter.fighterBelt || Belt.Colors.White,
            fighterBeltDegree: fighter.fighterBeltDegree || 0,
            fighterType: fighter.fighterType || FighterType.STUDENT,
            pointsLeft: fighter.pointsLeft,
            gameStyle: fighter.gameStyle,
            personalSkills: {
              technique: fighter.personalSkills.technique,
              strength: fighter.personalSkills.strength,
              agility: fighter.personalSkills.agility,
              stamina: fighter.personalSkills.stamina,
              commitment: fighter.personalSkills.commitment,
              focus: fighter.personalSkills.focus,
            },
            gameSkills: {
              guard: fighter.gameSkills.guard,
              guardRecovery: fighter.gameSkills.guardRecovery,
              guardPassing: fighter.gameSkills.guardPassing,
              sweep: fighter.gameSkills.sweep,
              takedown: fighter.gameSkills.takedown,
              submission: fighter.gameSkills.submission,
              submissionDefense: fighter.gameSkills.submissionDefense,
              positionDefense: fighter.gameSkills.positionDefense,
            },
            isRated: fighter.isRated ? fighter.isRated : false,
          })),
          index: 0, // Add the missing index property
          feedbackActions: feedbackStore.feedbackActions,
        }
        feedbackStore.feedbackActions.setFeedback(newFeedback)
      } else if (currentStep === 'cards') {
        cardsStore.cardsActions.setCards(fetchedData as FighterCard[])
        cardsStore.cardsActions.setBeltAveragesAsBenchmark()
      }
    } finally {
      setIsLoading(false)
    }
  }, [
    phone,
    token,
    userPhone,
    userBelt,
    tableGroups,
    tableStore,
    feedbackStore,
    cardsStore,
    user,
    router.query?.avaliar,
  ])

  useEffect(() => {
    if (token && phone) getUserInfo()
  }, [token, phone])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsLoading(true)
    setFormError(null)

    const userPhoneField = document.getElementById('userPhoneDisplay')
    const beltField = document.getElementById('userBelt')
    const ssoCodeField = document.getElementById('ssoCode')

    if (!validateInputs(userPhoneField, beltField)) {
      setIsLoading(false)
      return
    }

    if (!ssoCodeConfig.show) {
      const nextStep = await handleInitialVerification()
      if (!nextStep) {
        setIsLoading(false)
        return
      }
    } else {
      const isVerified = await handleCodeVerification(ssoCodeField)
      if (!isVerified) {
        setIsLoading(false)
        return
      }
    }

    setIsLoading(false)
  }

  const validateInputs = (
    userPhoneField: HTMLElement | null,
    beltField: HTMLElement | null,
  ) => {
    let valid = true

    if (!userPhone) {
      userPhoneField?.classList.add('field-error')
      valid = false
    }

    if (!userBelt) {
      beltField?.classList.add('field-error')
      valid = false
    }

    return valid
  }

  const handleInitialVerification = async () => {
    const verifyNumber = await verifyPhoneLogin(userPhone)

    if (!verifyNumber?.verify && !isRecoverPassword) {
      setSsoCodeConfig((prev) => ({ ...prev, show: true, screen: 'login' }))
      return false
    }

    const ssoCodeSent = isRecoverPassword
      ? await sendRecoverPasswordCode(userPhone)
      : await sendSsoCode(userPhone)

    if (!ssoCodeSent?.ok) {
      if (ssoCodeSent?.status === 404) {
        window.location.href = '/avaliacao/adicione-a-jiulia'
        return false
      }

      setFormError(
        ssoCodeSent?.message ??
          'Não foi possível enviar o código agora. Verifique sua conexão e tente novamente.',
      )
      return false
    }

    setSsoCodeConfig((prev) => ({
      ...prev,
      show: true,
      screen: 'sendCode',
    }))

    return true
  }

  const handleCodeVerification = async (
    ssoCodeField: HTMLElement | null,
  ): Promise<boolean> => {
    if (!ssoCodeConfig?.code) {
      ssoCodeField?.classList.add('field-error')
      setFormError(t('identificatorBox.ssoCodeInvalid') as string)
      return false
    }

    const codeVerified = isRecoverPassword
      ? await verifyRecoverPassword(userPhone, ssoCodeConfig?.code)
      : await verifySsoCode(userPhone, ssoCodeConfig?.code)

    if (!codeVerified) {
      ssoCodeField?.classList.add('field-error')
      setFormError(t('identificatorBox.ssoCodeInvalid') as string)
      return false
    }

    login(codeVerified.token)
    return true
  }

  return (
    <div className="bg-white/20 border-1 border-white/15 p-8 md:px-10 md:py-12 rounded-3xl shadow-lg lg:w-1/2 mt-2 md:my-10 backdrop-blur-sm">
      <div
        id="spinningLoader"
        className={isLoading ? 'flex align-center' : 'hidden'}
      >
        <div>{t('identificatorBox.loading')}</div>
      </div>
      {formError ? (
        <div className="mb-6 rounded-xl border border-red-400/40 bg-red-400/20 px-4 py-3 text-white/90">
          {formError}
        </div>
      ) : null}
      <h2 className="text-4xl text-center text-white/90 mb-3">
        {t('identificatorBox.formTitle')}
      </h2>
      <p className="text-center text-white/60 mb-10 text-lg">
        {t('identificatorBox.formSubtitle')}
      </p>
      <form className="flex flex-col gap-8" onSubmit={handleSubmit}>
        <div id="identificationFields" className="flex flex-col gap-4">
          <div className="w-full">
            <CountryPhoneInput
              onChange={(phone) => setUserPhone(phone)}
              phone={userPhone}
            />
          </div>
          <div className="w-full">
            <BeltInput onChange={(belt) => setUserBelt(belt)} belt={userBelt} />
          </div>
          <div className="w-full">
            <SsoCodeInput
              hidden={!ssoCodeConfig.show}
              handleChange={(value) =>
                setSsoCodeConfig((prev) => ({ ...prev, code: value }))
              }
              label={
                ssoCodeConfig?.screen === 'sendCode'
                  ? t('identificatorBox.ssoCodeSms')
                  : t('identificatorBox.ssoCode')
              }
              value={ssoCodeConfig.code}
            />
          </div>
        </div>
        <div className="w-full text-center flex flex-col gap-2">
          <input
            type="submit"
            value={
              isRecoverPassword
                ? t('identificatorBox.recoverPassword')?.toString()
                : t('identificatorBox.formLabels.button')?.toString()
            }
            className="button bg-red-100 rounded-full w-full sm:w-auto px-10 py-3.5 text-center transition-all duration-300 ease-in-out display-inline-block cursor-pointer shadow-lg hover:shadow-xl text-white hover:bg-red-300 focus:outline-none font-medium"
          />
          <input
            type="submit"
            hidden={isRecoverPassword}
            value={t('identificatorBox.recoverPassword')?.toString()}
            onClick={(e) => {
              e.preventDefault()
              setSsoCodeConfig({
                show: false,
                screen: 'sendCode',
                code: '',
              })
              setIsRecoverPassword(true)
            }}
            className="button text-white/60 hover:text-white transition-all duration-300 ease-in-out"
          />
        </div>

        <div className="border-t border-white/30 mt-2 pt-10">
          <h5 className="text-center text-white/80 mb-3">
            ⚠️ {t('identificatorBox.formReminder')}
          </h5>
          <p className="text-center text-white/80 mx-auto text-sm">
            <a
              href="https://jiulia.goodfellas.app/"
              target="_blank"
              rel="noreferrer"
              className="bg-red-400/50 text-white p-1 border-b border-white/30"
            >
              {t('identificatorBox.formReminderText')}
            </a>
          </p>
        </div>
      </form>
    </div>
  )
}
